import { useEffect, useMemo, useState } from "react";
import { Building2, ChevronRight, MapPin, Plus, Save, Users } from "lucide-react";
import { createCompany, updateCompany } from "../services/companyService";
import { createCompanyContact } from "../services/monthlyProcessService";

const emptyCompany = {
  legalName: "",
  shortName: "",
  taxNumber: "",
  taxOffice: "",
  mersisNumber: "",
  cariCode: "",
  paymentTermDays: 0,
  currency: "TRY",
  phone: "",
  email: "",
  website: "",
  address: "",
  notes: "",
  branches: [],
  isActive: true
};

const normalizeName = (value) =>
  String(value || "").trim().toLocaleLowerCase("tr-TR").replace(/\s+/g, " ");

function CompaniesView({
  companies = [],
  projects = [],
  contacts = [],
  technoparkOptions = [],
  canEdit = false,
  onContactsChange,
  themeMode = "light"
}) {
  const [selectedId, setSelectedId] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyCompany);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [branchForm, setBranchForm] = useState({ name: "", technoparkName: "", address: "", startDate: "", portalUrl: "" });
  const [contactForm, setContactForm] = useState({ contactName: "", title: "", phone: "", email: "", branchId: "" });
  const isDark = themeMode === "dark";

  const palette = {
    page: isDark ? "#0f172a" : "#f5f7fb",
    card: isDark ? "#162235" : "#fff",
    soft: isDark ? "#1e293b" : "#f8fafc",
    border: isDark ? "#334155" : "#e2e8f0",
    text: isDark ? "#e5edf6" : "#172033",
    muted: isDark ? "#94a3b8" : "#64748b",
    primary: "#124254"
  };

  const selectedCompany = companies.find((item) => item.id === selectedId) || null;

  useEffect(() => {
    if (isCreating) return;
    if (!selectedId && companies[0]?.id) setSelectedId(companies[0].id);
    if (selectedId && !companies.some((item) => item.id === selectedId)) {
      setSelectedId(companies[0]?.id || "");
    }
  }, [companies, isCreating, selectedId]);

  useEffect(() => {
    if (isCreating) {
      setForm(emptyCompany);
      return;
    }
    if (selectedCompany) {
      setForm({ ...emptyCompany, ...selectedCompany, branches: selectedCompany.branches || [] });
    }
  }, [isCreating, selectedCompany]);

  const filteredCompanies = useMemo(() => {
    const term = normalizeName(search);
    if (!term) return companies;
    return companies.filter((item) =>
      [item.legalName, item.shortName, item.taxNumber, item.cariCode]
        .some((value) => normalizeName(value).includes(term))
    );
  }, [companies, search]);

  const companyProjects = useMemo(() => {
    if (!selectedCompany) return [];
    const legalName = normalizeName(selectedCompany.legalName);
    const shortName = normalizeName(selectedCompany.shortName);
    return projects.filter((project) =>
      project.companyId === selectedCompany.id ||
      normalizeName(project.firmaAdi) === legalName ||
      normalizeName(project.firmaKisaAdi) === shortName
    );
  }, [projects, selectedCompany]);

  const companyContacts = useMemo(() => {
    if (!selectedCompany) return [];
    const legalName = normalizeName(selectedCompany.legalName);
    return contacts.filter((contact) =>
      contact.companyId === selectedCompany.id || normalizeName(contact.companyName) === legalName
    );
  }, [contacts, selectedCompany]);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 12px",
    borderRadius: 10,
    border: `1px solid ${palette.border}`,
    background: palette.card,
    color: palette.text,
    outline: "none"
  };

  const label = (title, field, props = {}) => (
    <label style={{ display: "grid", gap: 6, color: palette.muted, fontSize: 12, fontWeight: 800 }}>
      {title}
      <input
        {...props}
        value={form[field] ?? ""}
        disabled={!canEdit}
        onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
        style={inputStyle}
      />
    </label>
  );

  const saveCompany = async () => {
    setSaving(true);
    setMessage("");
    try {
      if (isCreating) {
        const result = await createCompany(form);
        setSelectedId(result.id);
        setIsCreating(false);
      } else {
        await updateCompany(selectedId, form);
      }
      setMessage("Firma profili kaydedildi.");
    } catch (error) {
      setMessage(error.message || "Firma kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const addBranch = async () => {
    if (!branchForm.name.trim() || !branchForm.technoparkName.trim()) {
      setMessage("Şube adı ve teknopark seçimi zorunludur.");
      return;
    }
    const nextForm = {
      ...form,
      branches: [
        ...(form.branches || []),
        { ...branchForm, id: crypto.randomUUID(), isActive: true, endDate: "" }
      ]
    };

    if (isCreating || !selectedId) {
      setForm(nextForm);
      setBranchForm({ name: "", technoparkName: "", address: "", startDate: "", portalUrl: "" });
      setMessage("Şube firma profiline eklendi. Yeni firma kaydını tamamlamak için Firma Profilini Kaydet'e basın.");
      return;
    }

    setSaving(true);
    setMessage("");
    try {
      await updateCompany(selectedId, nextForm);
      setForm(nextForm);
      setBranchForm({ name: "", technoparkName: "", address: "", startDate: "", portalUrl: "" });
      setMessage("Teknopark şubesi kaydedildi.");
    } catch (error) {
      setMessage(error.message || "Teknopark şubesi kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const addContact = async () => {
    if (!selectedCompany || !contactForm.contactName.trim()) return;
    setSaving(true);
    try {
      await createCompanyContact({
        ...contactForm,
        companyId: selectedCompany.id,
        companyName: selectedCompany.legalName,
        companyShortName: selectedCompany.shortName,
        note: ""
      });
      setContactForm({ contactName: "", title: "", phone: "", email: "", branchId: "" });
      await onContactsChange?.();
      setMessage("Firma yetkilisi eklendi.");
    } catch (error) {
      setMessage(error.message || "Yetkili eklenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    ["profile", "Firma Profili"],
    ["finance", "Cari Bilgileri"],
    ["branches", `Teknopark Şubeleri (${form.branches?.length || 0})`],
    ["projects", `Projeler (${companyProjects.length})`],
    ["contacts", `Yetkililer (${companyContacts.length})`]
  ];

  return (
    <div style={{ color: palette.text }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <div>
          <h1 style={{ margin: 0 }}>Firmalar</h1>
          <p style={{ color: palette.muted, margin: "7px 0 0" }}>Firma, cari, teknopark şubesi ve proje ilişkilerini yönetin.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setIsCreating(true); setSelectedId(""); setActiveTab("profile"); }} style={{ border: 0, borderRadius: 11, padding: "11px 15px", background: palette.primary, color: "#fff", fontWeight: 900, cursor: "pointer", display: "flex", gap: 7, alignItems: "center" }}>
            <Plus size={17} /> Yeni Firma
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "280px minmax(0, 1fr)", gap: 18, minHeight: 620 }}>
        <aside style={{ background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 18, padding: 14 }}>
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Firma ara..." style={{ ...inputStyle, marginBottom: 12 }} />
          <div style={{ display: "grid", gap: 7 }}>
            {filteredCompanies.map((company) => (
              <button key={company.id} onClick={() => { setSelectedId(company.id); setIsCreating(false); setActiveTab("profile"); }} style={{ textAlign: "left", border: `1px solid ${selectedId === company.id ? "#5eb8b2" : palette.border}`, background: selectedId === company.id ? (isDark ? "#153846" : "#edfafa") : palette.soft, color: palette.text, borderRadius: 12, padding: 12, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
                <Building2 size={18} />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <strong style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{company.shortName || company.legalName}</strong>
                  <small style={{ color: palette.muted }}>{(company.branches || []).length} teknopark şubesi</small>
                </span>
                <ChevronRight size={16} />
              </button>
            ))}
            {!filteredCompanies.length && <div style={{ color: palette.muted, padding: 12 }}>Henüz firma kaydı yok.</div>}
          </div>
        </aside>

        <main style={{ background: palette.card, border: `1px solid ${palette.border}`, borderRadius: 18, padding: 22 }}>
          {!selectedCompany && !isCreating ? (
            <div style={{ minHeight: 400, display: "grid", placeItems: "center", color: palette.muted }}>Başlamak için yeni firma oluşturun.</div>
          ) : (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "flex-start" }}>
                <div>
                  <div style={{ color: palette.muted, fontSize: 12, fontWeight: 900 }}>{isCreating ? "YENİ FİRMA" : "FİRMA PROFİLİ"}</div>
                  <h2 style={{ margin: "5px 0 0" }}>{form.shortName || form.legalName || "İsimsiz Firma"}</h2>
                </div>
                {canEdit && (
                  <button disabled={saving} onClick={saveCompany} style={{ border: 0, borderRadius: 11, padding: "10px 14px", background: palette.primary, color: "#fff", fontWeight: 900, cursor: "pointer", display: "flex", alignItems: "center", gap: 7 }}>
                    <Save size={17} /> {saving ? "Kaydediliyor..." : "Firma Profilini Kaydet"}
                  </button>
                )}
              </div>

              {message && <div style={{ marginTop: 14, padding: 11, borderRadius: 10, background: isDark ? "#1e3a4a" : "#eef8fa", color: isDark ? "#dff7ff" : "#124254", fontWeight: 800 }}>{message}</div>}

              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", margin: "20px 0" }}>
                {tabs.map(([id, title]) => (
                  <button key={id} onClick={() => setActiveTab(id)} disabled={isCreating && ["projects", "contacts"].includes(id)} style={{ border: `1px solid ${palette.border}`, borderRadius: 999, padding: "8px 12px", background: activeTab === id ? palette.primary : palette.soft, color: activeTab === id ? "#fff" : palette.text, fontWeight: 800, cursor: "pointer" }}>{title}</button>
                ))}
              </div>

              {activeTab === "profile" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {label("Ticari Unvan", "legalName")}
                  {label("Kısa Ad", "shortName")}
                  {label("Telefon", "phone")}
                  {label("E-posta", "email", { type: "email" })}
                  {label("Web Sitesi", "website")}
                  <label style={{ display: "grid", gap: 6, color: palette.muted, fontSize: 12, fontWeight: 800 }}>Durum<select disabled={!canEdit} value={form.isActive ? "active" : "passive"} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.value === "active" }))} style={inputStyle}><option value="active">Aktif</option><option value="passive">Pasif</option></select></label>
                  <label style={{ gridColumn: "1 / -1", display: "grid", gap: 6, color: palette.muted, fontSize: 12, fontWeight: 800 }}>Merkez Adresi<textarea disabled={!canEdit} value={form.address || ""} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} style={{ ...inputStyle, minHeight: 80 }} /></label>
                  <label style={{ gridColumn: "1 / -1", display: "grid", gap: 6, color: palette.muted, fontSize: 12, fontWeight: 800 }}>Notlar<textarea disabled={!canEdit} value={form.notes || ""} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} style={{ ...inputStyle, minHeight: 90 }} /></label>
                </div>
              )}

              {activeTab === "finance" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                  {label("Vergi Numarası", "taxNumber")}{label("Vergi Dairesi", "taxOffice")}{label("MERSİS Numarası", "mersisNumber")}{label("Cari Kodu", "cariCode")}{label("Ödeme Vadesi (gün)", "paymentTermDays", { type: "number", min: 0 })}
                  <label style={{ display: "grid", gap: 6, color: palette.muted, fontSize: 12, fontWeight: 800 }}>Para Birimi<select disabled={!canEdit} value={form.currency || "TRY"} onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))} style={inputStyle}><option value="TRY">TRY</option><option value="USD">USD</option><option value="EUR">EUR</option></select></label>
                </div>
              )}

              {activeTab === "branches" && (
                <div>
                  <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
                    {(form.branches || []).map((branch) => (
                      <div key={branch.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: 13, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.soft }}><MapPin size={18} /><div><strong>{branch.name}</strong><div style={{ color: palette.muted, fontSize: 13 }}>{branch.technoparkName}{branch.address ? ` • ${branch.address}` : ""}</div></div></div>
                    ))}
                  </div>
                  {canEdit && <div style={{ padding: 16, borderRadius: 14, background: palette.soft, border: `1px solid ${palette.border}` }}><h3 style={{ marginTop: 0 }}>Teknopark Şubesi Ekle</h3><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}><input placeholder="Şube/Lokasyon adı" value={branchForm.name} onChange={(e) => setBranchForm({ ...branchForm, name: e.target.value })} style={inputStyle} /><select aria-label="Teknopark seçin" value={branchForm.technoparkName} onChange={(e) => setBranchForm({ ...branchForm, technoparkName: e.target.value })} style={inputStyle}><option value="">Teknopark seçin</option>{technoparkOptions.map((name) => <option key={name} value={name}>{name}</option>)}</select><input placeholder="Şube adresi" value={branchForm.address} onChange={(e) => setBranchForm({ ...branchForm, address: e.target.value })} style={inputStyle} /><input type="date" value={branchForm.startDate} onChange={(e) => setBranchForm({ ...branchForm, startDate: e.target.value })} style={inputStyle} /><input placeholder="Portal adresi" value={branchForm.portalUrl} onChange={(e) => setBranchForm({ ...branchForm, portalUrl: e.target.value })} style={{ ...inputStyle, gridColumn: "1 / -1" }} /></div><button disabled={saving} onClick={addBranch} style={{ marginTop: 11, border: 0, borderRadius: 10, padding: "10px 13px", background: palette.primary, color: "#fff", fontWeight: 900, cursor: saving ? "wait" : "pointer", opacity: saving ? 0.7 : 1 }}><Plus size={15} style={{ verticalAlign: "middle", marginRight: 5 }} />{saving ? "Kaydediliyor..." : "Şube Ekle ve Kaydet"}</button></div>}
                </div>
              )}

              {activeTab === "projects" && <div style={{ display: "grid", gap: 9 }}>{companyProjects.map((project) => <div key={project.id} style={{ padding: 13, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.soft }}><strong>{project.projeAdi || "İsimsiz Proje"}</strong><div style={{ color: palette.muted, fontSize: 13, marginTop: 3 }}>{project.teknoparkAdi || "Teknopark belirtilmemiş"} • {project.projeStbKodu || "STB kodu yok"}</div></div>)}{!companyProjects.length && <div style={{ color: palette.muted }}>Bu firmaya bağlı proje bulunmuyor.</div>}</div>}

              {activeTab === "contacts" && (
                <div><div style={{ display: "grid", gap: 9, marginBottom: 18 }}>{companyContacts.map((contact) => <div key={contact.id} style={{ display: "flex", gap: 11, alignItems: "center", padding: 13, borderRadius: 12, border: `1px solid ${palette.border}`, background: palette.soft }}><Users size={18} /><div><strong>{contact.contactName}</strong><div style={{ color: palette.muted, fontSize: 13 }}>{contact.title || "Görev belirtilmemiş"}{contact.email ? ` • ${contact.email}` : ""}</div></div></div>)}{!companyContacts.length && <div style={{ color: palette.muted }}>Henüz firma yetkilisi eklenmemiş.</div>}</div>{canEdit && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, padding: 16, borderRadius: 14, background: palette.soft, border: `1px solid ${palette.border}` }}><input placeholder="Ad soyad" value={contactForm.contactName} onChange={(e) => setContactForm({ ...contactForm, contactName: e.target.value })} style={inputStyle} /><input placeholder="Görev/Unvan" value={contactForm.title} onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })} style={inputStyle} /><input placeholder="Telefon" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} style={inputStyle} /><input placeholder="E-posta" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} style={inputStyle} /><select value={contactForm.branchId} onChange={(e) => setContactForm({ ...contactForm, branchId: e.target.value })} style={{ ...inputStyle, gridColumn: "1 / -1" }}><option value="">Firma geneli</option>{(form.branches || []).map((branch) => <option key={branch.id} value={branch.id}>{branch.name} – {branch.technoparkName}</option>)}</select><button onClick={addContact} style={{ gridColumn: "1 / -1", border: 0, borderRadius: 10, padding: 10, background: palette.primary, color: "#fff", fontWeight: 900, cursor: "pointer" }}>Yetkili Ekle</button></div>}</div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default CompaniesView;
